import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TableStructure from "../../CommonComponents/TableStructure";
import { useToast} from '@chakra-ui/react';
import { getEventList } from "../../services/EventList";
import { showErrorToast } from "../../util/errorHandler";


const EventList = () => {
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchText, setSearchText] = useState('');
  const session = useSelector((state) => state.session);
  const isInitialLoad = useRef(true);
  const toast = useToast();
  const navigate = useNavigate();
  const limit = 50; // Number of records per page
  
  // Fetch events from the API
  const fetchEvents = async (page, term) => {
    setLoading(true);
    try {
      const response = await getEventList(limit, page, term);

      if (response?.data?.data) {
        const eventData = response.data.data.map(event => ({
          id: event.id || event._id,
          alias: event.alias,
          event_title: event.event_title,
          event_sub_title: event.event_sub_title,
          actual_date_start: event.actual_date_start ? event.actual_date_start : 'N/A',
          event_duration: event.event_duration || 'N/A',
          venue: event.venue || 'N/A',
        }));
        
        setRecords(eventData);
        setTotalPages(Math.ceil(response.data.total / limit) || 1);
        setTotalRecords(response.data.total || eventData.length);
      } else {
        setRecords([]);
        setTotalPages(1);
        setTotalRecords(0);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
            showErrorToast(toast, "Failed to fetch event list");
      setLoading(false);
      setRecords([]);
    }
  };

  // Handle search button click
  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false; // Skip the first render
      setCurrentPage(1); // Reset to page 1 for a new search
      fetchEvents(1, searchTerm);
      setSearchText(searchTerm);
    }
  };

  // Handle clear search
  const handleClear = () => {
    fetchEvents(1, "");
    setSearchText('');
  };

  const sortRecords = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc"; // Update sortOrder first
    setSortOrder(newSortOrder);
  
    const sorted = [...records].sort((a, b) =>
      newSortOrder === "asc"
        ? a.event_title.localeCompare(b.event_title)
        : b.event_title.localeCompare(a.event_title)
    );
  
    setRecords(sorted);
  };

  useEffect(() => {
    fetchEvents(currentPage, searchText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Handle page changes
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };

  // Handle row click to navigate to edit page
  const handleRowClick = (record) => {
    if (record && record.alias) {
      navigate(`/events/${record.alias}`);
    }
  };

  // Define table columns
  const columns = [
    {
      accessor: 'event_title',
      label: 'Event Title'
    },
    {
      accessor: 'event_sub_title',
      label: 'Event Sub Title'
    },
    {
      accessor: 'actual_date_start',
      label: 'Event Date'
    },
    {
      accessor: 'event_duration',
      label: 'Event Duration'
    },
    {
      accessor: 'venue',
      label: 'Venue'
    }
  ];

  return (
    <>
      <TableStructure
        isAuthenticated={session?.isAuthenticated}
        handleSearch={handleSearch}
        handleClear={handleClear}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        totalPages={totalPages}
        sortRecords={sortRecords}
        sortOrder={sortOrder}
        loading={loading}
        records={records}
        totalRecords={totalRecords}
        type="events"
        title="Events"
        columns={columns}
        pagination={true}
        disableRowClick={false}
        handleRowClick={handleRowClick}
        addOption={true}
      />
    </>
  );
};

export default EventList; 